/** Small presentational building blocks used across screens. */
import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useShell } from '../layouts/ShellContext.jsx';
import { AppIcon } from '../layouts/Brand.jsx';
import { Icon } from './Icon.jsx';

/**
 * A card. `subtitle` (or `description`) sits under the title; `style` is
 * applied to the card itself.
 */
export function Card({
  title,
  subtitle,
  description,
  actions,
  footer,
  children,
  className = '',
  bodyClassName = 'card-body',
  style,
}) {
  const sub = subtitle ?? description;
  return (
    <section className={`card ${className}`} style={style}>
      {(title || actions) && (
        <header className="card-header">
          <div className="card-heading">
            {title && <h2 className="card-title">{title}</h2>}
            {sub && <p className="card-subtitle">{sub}</p>}
          </div>
          {actions && <div className="row card-actions">{actions}</div>}
        </header>
      )}
      <div className={bodyClassName}>{children}</div>
      {footer && <div className="card-footer">{footer}</div>}
    </section>
  );
}

const textOf = (node) => (typeof node === 'string' || typeof node === 'number' ? String(node) : null);

/**
 * A page's title block: the trail above it, the title with an optional badge
 * and a pin, the subtitle, and the page's actions.
 *
 * On the console's home it becomes the console's front door, wearing its tile
 * and hue as every app home in NexERP does.
 */
export function PageHeader({ title, subtitle, description, breadcrumb, breadcrumbs, badge, actions, leading }) {
  const shell = useShell();
  const location = useLocation();
  const sub = subtitle ?? description;
  const trail = (breadcrumb ?? breadcrumbs ?? []).filter(Boolean);

  // Tell the shell what this page is called: the tab title and "Jump back in"
  // then name the organization ("Acme Ltd"), not the list it was opened from.
  const titleText = textOf(title);
  const setPageTitle = shell?.setPageTitle;
  useEffect(() => {
    if (setPageTitle && titleText) setPageTitle(titleText);
  }, [setPageTitle, titleText]);

  const { pathname } = location;
  const pinned = shell?.prefs?.isPinned(pathname) ?? false;
  const canPin = Boolean(shell?.prefs && titleText && pathname !== '/');

  const app = shell?.currentApp;
  const atHome = Boolean(app) && pathname === app.home;

  return (
    <header
      className={`page-header ${atHome ? 'page-header-app' : leading ? 'page-header-leading' : ''}`}
      data-app={atHome ? app.key : undefined}
    >
      {atHome && <AppIcon app={app} size="xl" />}
      {!atHome && leading}
      <div className="page-header-main">
        {trail.length > 0 && (
          <nav className="breadcrumb" aria-label="Breadcrumb">
            {trail.map((crumb, index) => {
              const last = index === trail.length - 1;
              return (
                <span key={`${textOf(crumb.label) ?? index}-${index}`} className="breadcrumb-item">
                  {index > 0 && (
                    <span className="breadcrumb-sep" aria-hidden="true">
                      <Icon name="chevron-right" size={12} strokeWidth={2} />
                    </span>
                  )}
                  {crumb.to ? (
                    <Link to={crumb.to}>{crumb.label}</Link>
                  ) : (
                    <span className="breadcrumb-current" aria-current={last ? 'page' : undefined}>
                      {crumb.label}
                    </span>
                  )}
                </span>
              );
            })}
          </nav>
        )}
        <div className="page-title-row">
          <h1 className="page-title">{title}</h1>
          {badge}
          {canPin && (
            <button
              type="button"
              className="page-pin"
              aria-pressed={pinned}
              aria-label={pinned ? 'Unpin this page' : 'Pin this page'}
              title={pinned ? 'Unpin from your navigation' : 'Pin to your navigation'}
              onClick={() => shell.prefs.togglePinned({ to: pathname, label: titleText })}
            >
              <Icon name="star" size={15} />
            </button>
          )}
        </div>
        {sub && <p className="page-subtitle">{sub}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}

/** A label/value pair on a detail screen. Supports both children and `value` prop. */
export function Detail({ label, children, value, mono }) {
  const content = children !== undefined ? children : value;
  const isEmpty = content === undefined || content === null || content === '';

  return (
    <div className="detail-item">
      <span className="detail-label">{label}</span>
      <span className={`detail-value ${mono ? 'mono' : ''}`}>
        {!isEmpty ? content : <span className="subtle">—</span>}
      </span>
    </div>
  );
}

export function DetailGrid({ children }) {
  return <div className="detail-grid">{children}</div>;
}

/**
 * A dashboard tile. Becomes a link when `to` is given.
 *
 * `tone` marks a figure that wants attention (warning, danger …) with a
 * coloured marker beside the label — the number itself stays in the text
 * colour, so the tone is never carried by colour alone. `hint` is another
 * name for `meta`; `icon` names the thing counted.
 */
export function Stat({ label, value, meta, hint, trend, to, tone, icon }) {
  const note = meta ?? hint;
  const body = (
    <>
      <span className="stat-label">
        {icon && <Icon name={icon} size={14} className="stat-icon" />}
        {label}
      </span>
      {tone && <span className={`stat-tone stat-tone-${tone}`} aria-hidden="true" />}
      <span className="stat-value">{value}</span>
      {trend && <span className="stat-trend">{trend}</span>}
      {note && <span className="stat-meta">{note}</span>}
    </>
  );

  if (to) {
    return (
      <Link to={to} className="stat stat-link">
        {body}
        <span className="stat-go" aria-hidden="true">
          <Icon name="arrow-up-right" size={15} />
        </span>
      </Link>
    );
  }

  return <div className="stat">{body}</div>;
}

const initialsOf = (source) => {
  const parts = String(source).trim().split(/\s+/).filter(Boolean);
  return (parts.length > 1 ? `${parts[0][0]}${parts.at(-1)[0]}` : String(source).slice(0, 2)).toUpperCase();
};

/** A person: a round avatar with their initials. */
export function Avatar({ name, email, large }) {
  const source = name || email || '?';
  return (
    <span className={`avatar ${large ? 'avatar-lg' : ''}`} aria-hidden="true">
      {initialsOf(source)}
    </span>
  );
}

/**
 * An organization: a square tile with its initials, tinted by one of the
 * categorical hues so a list of them can be told apart at a glance. The tint
 * is picked from the name, so the same organization always wears the same
 * one; it identifies, it does not mean anything.
 */
export function OrgAvatar({ name, size = 'md' }) {
  const source = name || '?';
  let hash = 0;
  for (const char of String(source)) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return (
    <span className={`org-avatar org-avatar-${size}`} data-tint={(hash % 8) + 1} aria-hidden="true">
      {initialsOf(source)}
    </span>
  );
}

/**
 * A horizontal bar row, drawn without a chart library.
 *
 * Bars of one list are one series, so they share one hue (series slot 1) and
 * length carries the value; `tone` picks a status colour instead, for a bar
 * that MEANS good or bad. The value is always printed beside the bar.
 */
export function BarRow({ label, value, max, formatted, tone }) {
  const pct = max > 0 ? Math.round((Number(value) / Number(max)) * 100) : 0;
  const fill = tone && tone !== 'accent' ? `var(--c-${tone})` : undefined;

  return (
    <div className="bar-row">
      <span className="truncate" title={label}>
        {label}
      </span>
      <span className="bar-track">
        <span className="bar-fill" style={{ width: `${Math.max(pct, value > 0 ? 2 : 0)}%`, background: fill }} />
      </span>
      <span className="num strong">{formatted ?? value}</span>
    </div>
  );
}
